import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CheckAnswer } from "@shared/domain";
import type { CheckTaskDTO } from "@shared/types";

/*
 * A check being run, saved on this phone on every tap. A phone killed in a
 * room with no signal opens again exactly where it stopped: the list, the
 * answers, the open section. Cleared once the submit is safely queued.
 */

export type DraftAnswer = { answer: CheckAnswer; note?: string; qty?: number };

export type TaskDraft = {
  /** The check as last fetched: enough to run it offline. */
  task: CheckTaskDTO | null;
  answers: Record<string, DraftAnswer>;
  linen: Record<string, number>;
  consumables: Record<string, number>;
  /** "" closes every section; null opens the first unfinished one. */
  openSection: string | null;
  /** Minted on the first submit and kept: a retried submit is the same submit. */
  submitKey?: string;
  updatedAt: number;
};

type State = {
  drafts: Record<string, TaskDraft>;
  /** Keep the latest copy of a check, without touching its answers. */
  remember: (task: CheckTaskDTO) => void;
  patch: (taskId: string, patch: Partial<Omit<TaskDraft, "task">>) => void;
  answer: (taskId: string, itemId: string, a: DraftAnswer | null) => void;
  clear: (taskId: string) => void;
};

const empty = (): TaskDraft => ({ task: null, answers: {}, linen: {}, consumables: {}, openSection: null, updatedAt: Date.now() });

export const useTaskDraft = create<State>()(
  persist(
    (set) => ({
      drafts: {},
      remember: (task) => set((s) => ({ drafts: { ...s.drafts, [task.id]: { ...(s.drafts[task.id] ?? empty()), task } } })),
      patch: (taskId, patch) => set((s) => ({ drafts: { ...s.drafts, [taskId]: { ...(s.drafts[taskId] ?? empty()), ...patch, updatedAt: Date.now() } } })),
      answer: (taskId, itemId, a) =>
        set((s) => {
          const d = s.drafts[taskId] ?? empty();
          const answers = { ...d.answers };
          if (a) answers[itemId] = a;
          else delete answers[itemId];
          return { drafts: { ...s.drafts, [taskId]: { ...d, answers, updatedAt: Date.now() } } };
        }),
      clear: (taskId) =>
        set((s) => {
          const drafts = { ...s.drafts };
          delete drafts[taskId];
          return { drafts };
        }),
    }),
    {
      name: "kk-task-drafts",
      // A draft untouched for a week is from a check long since done elsewhere.
      merge: (persisted, current) => {
        const drafts = (persisted as { drafts?: Record<string, TaskDraft> })?.drafts ?? {};
        const fresh = Object.fromEntries(Object.entries(drafts).filter(([, d]) => Date.now() - d.updatedAt < 7 * 24 * 60 * 60_000));
        return { ...current, drafts: fresh };
      },
    },
  ),
);

/** Answers saved on this phone that haven't gone to the server yet, for the offline pill. */
export const savedAnswerCount = (drafts: Record<string, TaskDraft>) => Object.values(drafts).reduce((n, d) => n + Object.keys(d.answers).length, 0);
